import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CSVImportModal from './CSVImportModal';
import Pagination from './Pagination';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactsDirectoryEnhanced = () => {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showBatchActions, setShowBatchActions] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [stats, setStats] = useState({
    total: 0,
    commercial: 0,
    technical: 0,
    billing: 0,
    management: 0
  });

  // Contact roles
  const contactRoles = [
    { value: 'Commercial', label: 'Commercial', icon: '💼' },
    { value: 'Technical', label: 'Technique', icon: '🔧' },
    { value: 'Billing', label: 'Facturation', icon: '💰' },
    { value: 'Management', label: 'Direction', icon: '👔' },
    { value: 'Operations', label: 'Opérations/Logistique', icon: '📦' }
  ];

  useEffect(() => {
    fetchContacts();
    fetchCompanies();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [contacts]);

  useEffect(() => {
    setShowBatchActions(selectedContacts.length > 0);
  }, [selectedContacts]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      console.log('Fetching companies from API...');
      const response = await axios.get(`${API}/crm/clients`);
      console.log('Companies loaded:', response.data?.length || 0);
      setCompanies(response.data || []);
      
      if (!response.data || response.data.length === 0) {
        console.warn('Aucune société trouvée. Les utilisateurs ne pourront pas créer de contacts.');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
      
      // Afficher une alerte si les sociétés ne peuvent pas être chargées
      if (error.response?.status === 403) {
        alert('Accès non autorisé pour récupérer les sociétés. Vérifiez vos permissions.');
      } else if (error.response?.status === 500) {
        alert('Erreur serveur lors du chargement des sociétés. Contactez l\'administrateur.');
      } else if (!error.response) {
        alert('Impossible de charger les sociétés. Vérifiez votre connexion internet.');
      }
    }
  };

  const reloadData = async () => {
    await Promise.all([fetchContacts(), fetchCompanies()]);
    setSelectedContacts([]);
  };

  const calculateStats = () => {
    const stats = {
      total: contacts.length,
      commercial: contacts.filter(c => c.role === 'Commercial').length,
      technical: contacts.filter(c => c.role === 'Technical').length,
      billing: contacts.filter(c => c.role === 'Billing').length,
      management: contacts.filter(c => c.role === 'Management').length
    };
    setStats(stats);
  };

  const handleSearch = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('query', searchTerm);
      if (filterCompany) params.append('company_id', filterCompany);
      if (filterRole) params.append('role', filterRole);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/contacts/search?${params}`);
      setContacts(response.data);
      setSelectedContacts([]);
    } catch (error) {
      console.error('Error searching contacts:', error);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterCompany('');
    setFilterRole('');
    setFilterStatus('');
    setSelectedContacts([]);
    fetchContacts();
  };

  // Selection handlers
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(contact => contact.id));
    }
  };

  // Batch actions
  const handleBatchDelete = async () => {
    if (selectedContacts.length === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedContacts.length} contact(s) ?`)) {
      try {
        await axios.post(`${API}/contacts/batch-delete`, {
          contact_ids: selectedContacts
        });
        
        fetchContacts();
        setSelectedContacts([]);
        alert(`${selectedContacts.length} contact(s) supprimé(s) avec succès!`);
      } catch (error) {
        console.error('Error in batch delete:', error);
        alert('Erreur lors de la suppression multiple');
      }
    }
  };

  const handleBatchStatusChange = async (newStatus) => {
    if (selectedContacts.length === 0) return;
    
    try {
      await axios.post(`${API}/contacts/batch-update-status`, {
        contact_ids: selectedContacts,
        is_active: newStatus === 'active'
      });
      
      fetchContacts();
      setSelectedContacts([]);
      alert(`Statut de ${selectedContacts.length} contact(s) modifié avec succès!`);
    } catch (error) {
      console.error('Error in batch status update:', error);
      alert('Erreur lors de la modification du statut');
    }
  };

  const handleSubmit = async (contactData) => {
    try {
      // Validation côté frontend
      if (!contactData.nom || contactData.nom.trim() === '') {
        alert('Le nom du contact est obligatoire');
        return;
      }
      
      if (!contactData.company_id || contactData.company_id.trim() === '') {
        alert('Veuillez sélectionner une société');
        return;
      }
      
      if (!contactData.role || contactData.role.trim() === '') {
        alert('Le rôle du contact est obligatoire');
        return;
      }
      
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, contactData);
        alert('Contact modifié avec succès!');
      } else {
        await axios.post(`${API}/contacts`, contactData);
        alert('Contact créé avec succès!');
      }
      fetchContacts();
      setShowForm(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      
      // Améliorer les messages d'erreur
      let errorMessage = 'Erreur lors de la sauvegarde du contact';
      
      if (error.response?.status === 422) {
        const errorDetails = error.response.data;
        if (typeof errorDetails === 'string') {
          errorMessage = errorDetails;
        } else if (errorDetails.detail) {
          if (Array.isArray(errorDetails.detail)) {
            errorMessage = errorDetails.detail.map(err => err.msg || err).join(', ');
          } else {
            errorMessage = errorDetails.detail;
          }
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Société non trouvée. Veuillez sélectionner une société valide.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Données invalides. Vérifiez les informations saisies.';
      }
      
      alert(errorMessage);
    }
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
      try {
        await axios.delete(`${API}/contacts/${contactId}`);
        fetchContacts();
        alert('Contact supprimé avec succès!');
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert('Erreur lors de la suppression du contact');
      }
    }
  };

  // Filter contacts based on current filters
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = !searchTerm || 
      contact.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCompany = !filterCompany || contact.company_id === filterCompany;
    const matchesRole = !filterRole || contact.role === filterRole;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && contact.is_active) ||
      (filterStatus === 'inactive' && !contact.is_active);
    
    return matchesSearch && matchesCompany && matchesRole && matchesStatus;
  });

  // Group contacts by company for grid view
  const contactsByCompany = filteredContacts.reduce((acc, contact) => {
    const companyName = contact.company_name || 'Sans société';
    if (!acc[companyName]) {
      acc[companyName] = [];
    }
    acc[companyName].push(contact);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Chargement des contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Annuaire de Contacts</h1>
        <div className="flex gap-2">
          {/* Reload Data Button */}
          <button
            onClick={reloadData}
            disabled={loading}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition-colors text-sm border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Actualiser les données"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="spinner-sm mr-1"></div>
                <span className="hidden sm:inline">Chargement...</span>
              </div>
            ) : (
              <div className="flex items-center">
                <span className="text-base mr-1">🔄</span>
                <span className="hidden sm:inline">Reload</span>
              </div>
            )}
          </button>
          
          {/* Import CSV Button */}
          <button
            onClick={() => setShowImportModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
          >
            <div className="flex items-center">
              <span className="mr-1">📄</span>
              <span className="hidden sm:inline">Import CSV</span>
            </div>
          </button>
          
          {/* New Contact Button */}
          <button
            onClick={() => setShowForm(true)}
            className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors"
          >
            Nouveau Contact
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="text-2xl">👥</div>
          </div>
        </div>
        
        {contactRoles.map((role) => (
          <div key={role.value} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{role.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stats[role.value.toLowerCase()]}</p>
              </div>
              <div className="text-2xl">{role.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Advanced Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Rechercher un contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>
          
          <div>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Toutes les sociétés</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tous les rôles</option>
              {contactRoles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="flex-1 bg-baobab-primary text-white px-4 py-3 rounded-lg hover:bg-baobab-dark transition-colors text-sm"
            >
              🔍 Rechercher
            </button>
            <button
              onClick={resetFilters}
              className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              ↺ Reset
            </button>
          </div>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Vue:</span>
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-1 rounded text-sm ${viewMode === 'grid' ? 'bg-baobab-primary text-white' : 'bg-gray-200'}`}
          >
            Par société
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 rounded text-sm ${viewMode === 'list' ? 'bg-baobab-primary text-white' : 'bg-gray-200'}`}
          >
            Liste
          </button>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {showBatchActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-blue-700 font-medium">
                {selectedContacts.length} contact(s) sélectionné(s)
              </span>
              <button
                onClick={() => setSelectedContacts([])}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Tout désélectionner
              </button>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => handleBatchStatusChange('active')}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                ✓ Activer
              </button>
              <button
                onClick={() => handleBatchStatusChange('inactive')}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors text-sm"
              >
                ⏸ Désactiver
              </button>
              <button
                onClick={handleBatchDelete}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
              >
                🗑 Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showForm && (
        <ContactForm
          contact={editingContact}
          companies={companies}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingContact(null);
          }}
        />
      )}

      {/* Import CSV Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        type="contacts"
        onImportComplete={reloadData}
      />

      {/* Results */}
      <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
        <span>
          {filteredContacts.length} contact{filteredContacts.length > 1 ? 's' : ''} trouvé{filteredContacts.length > 1 ? 's' : ''}
        </span>
        {viewMode === 'list' && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
              onChange={handleSelectAll}
              className="rounded"
            />
            <span>Sélectionner tout</span>
          </div>
        )}
      </div>

      {/* Grid View - Grouped by Company */}
      {viewMode === 'grid' ? (
        <div className="space-y-6">
          {Object.entries(contactsByCompany).map(([companyName, companyContacts]) => (
            <div key={companyName} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200 bg-baobab-primary text-white">
                <h3 className="font-medium">{companyName} ({companyContacts.length})</h3>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {companyContacts.map(contact => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      isSelected={selectedContacts.includes(contact.id)}
                      onSelect={handleSelectContact}
                      onEdit={(contact) => {
                        setEditingContact(contact);
                        setShowForm(true);
                      }}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
          
          {Object.keys(contactsByCompany).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📞</div>
              <p>Aucun contact trouvé</p>
            </div>
          )}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-baobab-primary text-white">
                  <th className="p-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="p-3 text-left">Nom</th>
                  <th className="p-3 text-left">Fonction</th>
                  <th className="p-3 text-left">Société</th>
                  <th className="p-3 text-left">Rôle</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Téléphone</th>
                  <th className="p-3 text-left">Statut</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((contact) => (
                  <tr key={contact.id} className={`border-b border-gray-200 hover:bg-gray-50 ${
                    selectedContacts.includes(contact.id) ? 'bg-blue-50' : ''
                  }`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3 font-medium">
                      {contact.nom} {contact.prenom}
                    </td>
                    <td className="p-3">{contact.fonction || '-'}</td>
                    <td className="p-3">{contact.company_name}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(contact.role)}`}>
                        {contactRoles.find(r => r.value === contact.role)?.label || contact.role}
                      </span>
                    </td>
                    <td className="p-3">{contact.email || '-'}</td>
                    <td className="p-3">
                      {contact.telephone_mobile || contact.telephone || '-'}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        contact.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {contact.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingContact(contact);
                            setShowForm(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 px-2 py-1 text-sm"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="text-red-600 hover:text-red-800 px-2 py-1 text-sm"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredContacts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📞</div>
              <p>Aucun contact trouvé</p>
            </div>
          )}

          {/* Pagination */}
          {filteredContacts.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredContacts.length / itemsPerPage)}
              totalItems={filteredContacts.length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced Contact Card Component with selection
const ContactCard = ({ contact, isSelected, onSelect, onEdit, onDelete }) => {
  const contactRoles = [
    { value: 'Commercial', label: 'Commercial', icon: '💼' },
    { value: 'Technical', label: 'Technique', icon: '🔧' },
    { value: 'Billing', label: 'Facturation', icon: '💰' },
    { value: 'Management', label: 'Direction', icon: '👔' }
  ];

  const roleInfo = contactRoles.find(r => r.value === contact.role);

  return (
    <div className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
      isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(contact.id)}
            className="rounded"
          />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">
              {contact.nom} {contact.prenom}
            </h4>
            {contact.fonction && (
              <p className="text-sm text-gray-600">{contact.fonction}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-lg">{roleInfo?.icon}</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(contact.role)}`}>
            {roleInfo?.label || contact.role}
          </span>
        </div>
      </div>
      
      <div className="space-y-2 mb-4">
        {contact.email && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>📧</span>
            <span>{contact.email}</span>
          </div>
        )}
        {(contact.telephone_mobile || contact.telephone) && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>📱</span>
            <span>{contact.telephone_mobile || contact.telephone}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            contact.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {contact.is_active ? 'Actif' : 'Inactif'}
          </span>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(contact)}
          className="flex-1 bg-blue-500 text-white px-3 py-2 rounded text-sm hover:bg-blue-600 transition-colors"
        >
          Modifier
        </button>
        <button
          onClick={() => onDelete(contact.id)}
          className="bg-red-500 text-white px-3 py-2 rounded text-sm hover:bg-red-600 transition-colors"
        >
          Supprimer
        </button>
      </div>
    </div>
  );
};

// Contact Form Component (same as before)
const ContactForm = ({ contact, companies, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    nom: contact?.nom || '',
    prenom: contact?.prenom || '',
    fonction: contact?.fonction || '',
    email: contact?.email || '',
    telephone: contact?.telephone || '',
    telephone_mobile: contact?.telephone_mobile || '',
    role: contact?.role || 'Commercial',
    company_id: contact?.company_id || '',
    is_point_contact_principal: contact?.is_point_contact_principal || false,
    is_contact_logistique: contact?.is_contact_logistique || false,
    notes: contact?.notes || '',
    is_active: contact?.is_active !== undefined ? contact.is_active : true
  });

  const contactRoles = [
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Technical', label: 'Technique' },
    { value: 'Billing', label: 'Facturation' },
    { value: 'Management', label: 'Direction' },
    { value: 'Operations', label: 'Opérations/Logistique' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {contact ? 'Modifier le contact' : 'Nouveau contact'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom *
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom
              </label>
              <input
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fonction
              </label>
              <input
                type="text"
                name="fonction"
                value={formData.fonction}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone fixe
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone mobile
              </label>
              <input
                type="tel"
                name="telephone_mobile"
                value={formData.telephone_mobile}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rôle *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              >
                {contactRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Société *
              </label>
              <select
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              >
                <option value="">Sélectionner une société</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>

          {/* Nouveaux champs pour spécifier les types de contacts */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Type de contact spécialisé</h4>
            
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                name="is_point_contact_principal"
                checked={formData.is_point_contact_principal}
                onChange={handleChange}
                className="mr-3 h-4 w-4 text-baobab-primary focus:ring-baobab-primary border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                📞 Point de contact principal côté client
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_contact_logistique"
                checked={formData.is_contact_logistique}
                onChange={handleChange}
                className="mr-3 h-4 w-4 text-baobab-primary focus:ring-baobab-primary border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                📦 Responsable logistique/opération côté client
              </label>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              💡 Ces marquages permettent aux opérateurs de voir uniquement les contacts pertinents
            </p>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="mr-2"
            />
            <label className="text-sm font-medium text-gray-700">
              Contact actif
            </label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-baobab-primary text-white py-3 px-4 rounded-lg hover:bg-baobab-dark transition-colors"
            >
              {contact ? 'Modifier' : 'Créer'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Utility function for role colors
const getRoleColor = (role) => {
  switch (role) {
    case 'Commercial':
      return 'bg-blue-100 text-blue-800';
    case 'Technical':
      return 'bg-green-100 text-green-800';
    case 'Billing':
      return 'bg-yellow-100 text-yellow-800';
    case 'Management':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default ContactsDirectoryEnhanced;